import React, { useEffect, useState } from 'react';
// For GUID generation
// crypto.randomUUID() is available in modern browsers
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Container, Paper, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete } from '@mui/material';
import { GoogleSheetsService } from '../services/GoogleSheetsService';
import LoadingOverlay from 'react-loading-overlay-ts';
import useAuth from '../hooks/useAuth';
import moment from 'moment';

interface ShowResultRow {
  rowIndexId: number;
  division: string;
  riderName: string;
  horseName: string;
  time: string;
  showId: string;
}


const ShowResultDetailsPage: React.FC = () => {
  const { showId } = useParams<{ showId: string }>();
  const navigate = useNavigate();
  const user = useAuth();
  const [results, setResults] = useState<ShowResultRow[]>([]);
  const [classOrder, setClassOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addRow, setAddRow] = useState<ShowResultRow>({ rowIndexId: 0, division: '', riderName: '', horseName: '', time: '', showId: showId || '' });

  // Always update addRow.showId when dialog opens or showId changes
  useEffect(() => {
    if (addDialogOpen) {
      // If this is a new result (rowIndexId === 0), generate a new showId
      setAddRow(row => ({
        ...row,
        showId: row.rowIndexId === 0 ? (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`) : (showId || '')
      }));
    }
  }, [addDialogOpen, showId]);
  const [adding, setAdding] = useState(false);
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
  const [riderOptions, setRiderOptions] = useState<string[]>([]);
  const [horseOptions, setHorseOptions] = useState<string[]>([]);
  const [showName, setShowName] = useState<string>('');

  // Edit/Delete dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState<ShowResultRow | null>(null);
  const [editValues, setEditValues] = useState<ShowResultRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Reusable sort function
  const sortShowResults = (allResults: ShowResultRow[], classOrderArr: string[]): ShowResultRow[] => {
    const getBaseClass = (division: string) => division.split(' ')[0];
    const naturalCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    const parseTime = (t: string) => {
      if (!t) return Number.POSITIVE_INFINITY;
      const n = parseFloat(t);
      return isNaN(n) ? t : n;
    };
    return [...allResults].sort((a, b) => {
      const aBase = getBaseClass(a.division);
      const bBase = getBaseClass(b.division);
      const aIdx = classOrderArr.indexOf(aBase);
      const bIdx = classOrderArr.indexOf(bBase);
      const aOrder = aIdx === -1 ? 9999 : aIdx;
      const bOrder = bIdx === -1 ? 9999 : bIdx;
      if (aOrder !== bOrder) return aOrder - bOrder;
      if (a.division !== b.division) return naturalCompare(a.division, b.division);
      const aTime = parseTime(a.time);
      const bTime = parseTime(b.time);
      if (typeof aTime === 'number' && typeof bTime === 'number') return aTime - bTime;
      return String(aTime).localeCompare(String(bTime));
    });
  };

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);
      try {
         if (!user.user || !showId) throw new Error('User not authenticated or showId missing');
         const service = await GoogleSheetsService.fromFirebaseUser(user.user);
        if (!service) throw new Error('Failed to initialize Google Sheets service');
        // Fetch all show results for this showId
        const allResults = await service.getShowResultsByShowId(showId);
        // Fetch class order from Standings column G
        const classOrderArr = await service.getClassOrder();
        setClassOrder(classOrderArr);
        // Extract unique options for autocomplete from ALL shows (use new public method)
        const allShowResults = await service.getAllShowResults();
        // Divisions
        const uniqueDivisions: string[] = [...new Set(allShowResults.map((r: { division: string }) => r.division).filter(Boolean))];
        const getBaseClass = (division: string) => division.split(' ')[0];
        const naturalCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
        const sortedDivisions: string[] = uniqueDivisions.sort((a, b) => {
          const aBase = classOrderArr.indexOf(getBaseClass(a));
          const bBase = classOrderArr.indexOf(getBaseClass(b));
          const aOrder = aBase === -1 ? 9999 : aBase;
          const bOrder = bBase === -1 ? 9999 : bBase;
          if (aOrder !== bOrder) return aOrder - bOrder;
          return naturalCompare(a, b);
        });
        setDivisionOptions(sortedDivisions);
        // Rider names
        setRiderOptions([...new Set(allShowResults.map((r: { riderName: string }) => r.riderName).filter(Boolean))]);
        // Horse names
        setHorseOptions([...new Set(allShowResults.map((r: { horseName: string }) => r.horseName).filter(Boolean))]);
        // Fetch show name for this showId
        const allShows = await service.getAllShows();
        const show = allShows.find(s => s.showId === showId);
        setShowName(show?.showName || `START TIMES: 2pm\nOpen arena 1pm\nGate fee $5 per person (8 & under FREE)\nClasses offered and order of events...\nPee Wee Barrels $5\nYouth Barrels (15 & under) EF: $30\nOpen 4D Barrels EF: $40\nExhibitions: $5 each\nOffice Fee: $5 per entry (except pee wee & exhibitions)\nALL riders 12 & under must wear helmet\nPermits for non-members $10`);
        const sortedResults = sortShowResults(allResults, classOrderArr);
        setResults(sortedResults);
      } catch (err) {
        setError('Failed to load show results.');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [user, showId]);

  const handleAddChange = (field: keyof ShowResultRow, value: string) => {
    setAddRow({ ...addRow, [field]: value });
  };

  const handleAddSave = async () => {
    setAdding(true);
    try {
      if (!user.user || !showId) throw new Error('User not authenticated or showId missing');
      const service = await GoogleSheetsService.fromFirebaseUser(user.user);
      if (!service) throw new Error('Failed to initialize Google Sheets service');
      await service.addShowResult({
        division: addRow.division,
        riderName: addRow.riderName,
        horseName: addRow.horseName,
        time: addRow.time,
        showId: showId || '',
        showName: showName || ''
      });
      setAddDialogOpen(false);
      setAddRow({
        rowIndexId: 0,
        division: '',
        riderName: '',
        horseName: '',
        time: '',
        showId: window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`
      });
      // Refresh results
      const allResults = await service.getShowResultsByShowId(showId);
      // Use latest class order
      const classOrderArr = classOrder.length ? classOrder : await service.getClassOrder();
      // Sort division options by class order, then natural division order
      const uniqueDivisions = [...new Set(allResults.map(r => r.division).filter(Boolean))];
      const getBaseClass = (division: string) => division.split(' ')[0];
      const naturalCompare = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      const sortedDivisions = uniqueDivisions.sort((a, b) => {
        const aBase = getBaseClass(a);
        const bBase = getBaseClass(b);
        const aIdx = classOrderArr.indexOf(aBase);
        const bIdx = classOrderArr.indexOf(bBase);
        const aOrder = aIdx === -1 ? 9999 : aIdx;
        const bOrder = bIdx === -1 ? 9999 : bIdx;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return naturalCompare(a, b);
      });
      setDivisionOptions(sortedDivisions);
      setRiderOptions([...new Set(allResults.map(r => r.riderName).filter(Boolean))]);
      setHorseOptions([...new Set(allResults.map(r => r.horseName).filter(Boolean))]);
      const sortedResults = sortShowResults(allResults, classOrderArr);
      setResults(sortedResults);
    } catch (err) {
      setError('Failed to add show result.');
    } finally {
      setAdding(false);
    }
  };

  return (
    <LoadingOverlay active={loading} className="loading-overlay">
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ padding: 4, marginTop: 4 }}>
          <Button variant="outlined" color="secondary" sx={{ mb: 2 }} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
            Show Results Details
          </Typography>
          {error && <Typography color="error">{error}</Typography>}
          <Button variant="contained" color="primary" sx={{ mb: 2 }} onClick={() => setAddDialogOpen(true)}>
            Add Result
          </Button>
          {/* Table mimic Standings */}
          <Box sx={{ overflowX: 'auto' }}>
            <div style={{ display: 'flex', fontWeight: 'bold', marginBottom: 8, marginTop: 8, padding: '4px 0' }}>
              <div style={{ flex: '2 0 100px', minWidth: 80 }}>Division</div>
              <div style={{ flex: '3 0 120px', minWidth: 80 }}>Rider Name</div>
              <div style={{ flex: '3 0 120px', minWidth: 80 }}>Horse Name</div>
              <div style={{ flex: '2 0 80px', minWidth: 60 }}>Time</div>
            </div>
            {results.map((row, idx) => (
              <div
                key={row.rowIndexId}
                style={{ display: 'flex', alignItems: 'center', marginBottom: 4, padding: '4px 0', cursor: 'pointer' }}
                onClick={() => {
                  setEditRow(row);
                  setEditValues(row);
                  setEditDialogOpen(true);
                }}
              >
                <div style={{ flex: '2 0 100px', minWidth: 80 }}>{row.division}</div>
                <div style={{ flex: '3 0 120px', minWidth: 80 }}>{row.riderName}</div>
                <div style={{ flex: '3 0 120px', minWidth: 80 }}>{row.horseName}</div>
                <div style={{ flex: '2 0 80px', minWidth: 60 }}>{row.time}</div>
              </div>
            ))}
            {results.length === 0 && <Typography variant="body2">No results found for this show.</Typography>}
          </Box>

          {/* Add Result Dialog */}
          <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add Show Result</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Autocomplete
                  freeSolo
                  options={divisionOptions}
                  value={addRow.division}
                  onInputChange={(_, newValue) => handleAddChange('division', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Division" fullWidth />
                  )}
                />
                <Autocomplete
                  freeSolo
                  options={riderOptions}
                  value={addRow.riderName}
                  onInputChange={(_, newValue) => handleAddChange('riderName', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Rider Name" fullWidth />
                  )}
                />
                <Autocomplete
                  freeSolo
                  options={horseOptions}
                  value={addRow.horseName}
                  onInputChange={(_, newValue) => handleAddChange('horseName', newValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Horse Name" fullWidth />
                  )}
                />
                <TextField
                  label="Time"
                  value={addRow.time}
                  onChange={e => handleAddChange('time', e.target.value)}
                  fullWidth
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddDialogOpen(false)} color="secondary" variant="outlined" disabled={adding}>Cancel</Button>
              <Button onClick={handleAddSave} color="primary" variant="contained" disabled={adding}>Save</Button>
            </DialogActions>
          </Dialog>

          {/* Edit/Delete Dialog */}
          <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Edit or Delete Show Result</DialogTitle>
            <DialogContent>
              {editValues && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={divisionOptions}
                    value={editValues.division}
                    onInputChange={(_, newValue) => setEditValues({ ...editValues, division: newValue })}
                    renderInput={(params) => (
                      <TextField {...params} label="Division" fullWidth />
                    )}
                  />
                  <Autocomplete
                    freeSolo
                    options={riderOptions}
                    value={editValues.riderName}
                    onInputChange={(_, newValue) => setEditValues({ ...editValues, riderName: newValue })}
                    renderInput={(params) => (
                      <TextField {...params} label="Rider Name" fullWidth />
                    )}
                  />
                  <Autocomplete
                    freeSolo
                    options={horseOptions}
                    value={editValues.horseName}
                    onInputChange={(_, newValue) => setEditValues({ ...editValues, horseName: newValue })}
                    renderInput={(params) => (
                      <TextField {...params} label="Horse Name" fullWidth />
                    )}
                  />
                  <TextField
                    label="Time"
                    value={editValues.time}
                    onChange={e => setEditValues({ ...editValues, time: e.target.value })}
                    fullWidth
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditDialogOpen(false)} color="secondary" variant="outlined" disabled={deleting}>Cancel</Button>
              <Button onClick={() => setDeleteConfirmOpen(true)} color="error" variant="outlined" disabled={deleting}>Delete</Button>
              <Button
                onClick={async () => {
                  if (!user || !showId || !editRow || !editValues) return;
                  setLoading(true);
                  setError(null);
                  try {
                     if (!user.user) throw new Error('User not authenticated');
                     const service = await GoogleSheetsService.fromFirebaseUser(user.user);
                    if (!service) throw new Error('Failed to initialize Google Sheets service');
                    await service.updateShowResultByIndex(
                      editRow.rowIndexId,
                      {
                        division: editValues.division,
                        riderName: editValues.riderName,
                        horseName: editValues.horseName,
                        time: editValues.time,
                        showId: showId,
                        showName: showName || ''
                      }
                    );
                    setEditDialogOpen(false);
                    // Refresh results
                    const allResults = await service.getShowResultsByShowId(showId);
                    // Always use sortShowResults for consistency
                    const classOrderArr = classOrder.length ? classOrder : await service.getClassOrder();
                    setResults(sortShowResults(allResults, classOrderArr));
                  } catch (err) {
                    setError('Failed to update show result.');
                  } finally {
                    setLoading(false);
                  }
                }}
                color="primary"
                variant="contained"
                disabled={deleting || !editValues || !editValues.division || !editValues.riderName}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              <Typography>Are you sure you want to delete this show result?</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteConfirmOpen(false)} color="secondary" variant="outlined" disabled={deleting}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!user || !showId || !editRow) return;
                  setDeleting(true);
                  try {
                     if (!user.user) throw new Error('User not authenticated');
                     const service = await GoogleSheetsService.fromFirebaseUser(user.user);
                    if (!service) throw new Error('Failed to initialize Google Sheets service');
                    // rowIndexId is 0-based for API (header is row 0)
                    await service.deleteRowByIndex('Show Results', editRow.rowIndexId);
                    setDeleteConfirmOpen(false);
                    setEditDialogOpen(false);
                    // Refresh results
                    const allResults = await service.getShowResultsByShowId(showId);
                    const classOrderArr = classOrder.length ? classOrder : await service.getClassOrder();
                    setResults(sortShowResults(allResults, classOrderArr));
                  } catch (err) {
                    setError('Failed to delete show result.');
                  } finally {
                    setDeleting(false);
                  }
                }}
                color="error"
                variant="contained"
                disabled={deleting}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </LoadingOverlay>
  );
};

export default ShowResultDetailsPage;
